import { useState, useEffect } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Typography,
  Divider,
  FormControl,
  OutlinedInput,
  MenuItem,
  Select,
  InputLabel,
  alpha,
  Button,
} from '@mui/material';
import { useRouteLoaderData } from 'react-router-dom';
import tenant from '@/services/TenantService';

export default function GeneralUserSubscriptionsSettings({ app, deployment }) {
  const { users } = useRouteLoaderData('auth');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [error, setError] = useState(true);
  const [allowNoUsers, setAllowNoUsers] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);

  const handleUserChange = (event) => {
    setSelectedUsers(event.target.value);
  };

  const handleRemoveUser = (userId) => {
    setSelectedUsers((prevSelected) =>
      prevSelected.filter((id) => id !== userId),
    );
  };

  const handleCheckboxChange = (event) => {
    setAllowNoUsers(event.target.checked);
  };

  const handleSave = async () => {
    if (selectedUsers.length === 0 && !allowNoUsers) {
      setError(true);
    } else {
      setError(false);

      const { data: deploymentSubscriptions } = await tenant.get(
        `/subscription?deploymentId=${deployment._id}`,
      );

      const filteredSubscriptions = deploymentSubscriptions.filter(
        (sub) => sub.status === 'active' || sub.status === 'pending',
      );

      const usersToAdd =
        selectedUsers.filter(
          (user) =>
            !filteredSubscriptions?.some(
              (sub) => sub.userId.toString() === user,
            ),
        ) || [];

      if (usersToAdd.length > 0) {
        await tenant.post('/subscription', {
          deploymentId: deployment._id,
          userIds: usersToAdd,
        });
      }

      // TODO handle reactivation and deactivation
      // also need to find a way to activate subscriptions
    }
  };

  useEffect(() => {
    if (allowNoUsers) {
      setSelectedUsers([]);
      setError(false);
    } else if (selectedUsers.length === 0) {
      setError(true);
    } else {
      setError(false);
    }
  }, [allowNoUsers, selectedUsers.length]);

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: '20%',
      },
    },
  };

  const loadInitialData = async () => {
    const { data: subs } = await tenant.get(
      `/subscription?deploymentId=${deployment._id}`,
    );

    if (subs && subs.length > 0) {
      //   const activeSubs = subs.filter((s) => s.status === 'active');
      const userIds = subs.map((sub) => sub.userId.toString());
      setSelectedUsers(userIds);
      setSubscriptions(subs);
      setError(false);
    } else {
      setSelectedUsers([]);
      setError(true);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ my: 1, mb: 2, pr: '80px' }}>
        <Typography variant="h6">
          Benutzerverwaltung für das Pass-Tool
        </Typography>
        <Divider />
      </Box>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Wählen Sie alle Benutzer aus, die eine Lizenz für {app.title} erhalten
        sollen.
      </Typography>

      <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
        <InputLabel id="user-select-label">Benutzer</InputLabel>
        <Select
          labelId="user-select-label"
          id="user-select"
          multiple
          value={selectedUsers}
          onChange={handleUserChange}
          MenuProps={MenuProps}
          input={<OutlinedInput id="select-multiple-chip" label="Benutzer" />}
          disabled={allowNoUsers}
        >
          {users.map((user) => (
            <MenuItem key={user._id} value={user._id}>
              {`${user.firstName} ${user.lastName}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={allowNoUsers}
            onChange={handleCheckboxChange}
            name="allowNoUsers"
            color="secondary"
          />
        }
        label={
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Keine Benutzer auswählen
          </Typography>
        }
      />

      {error && selectedUsers.length === 0 && !allowNoUsers && (
        <Typography
          color="error"
          variant="caption"
          sx={{ display: 'block', mt: 1 }}
        >
          Bitte wählen Sie mindestens einen Benutzer aus oder aktivieren Sie die
          Option Keine Benutzer auswählen.
        </Typography>
      )}

      {selectedUsers.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Ausgewählte Benutzer:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mt: 1,
              p: 2,
              borderRadius: 1,
              bgcolor: (theme) => alpha(theme.palette.background.default, 0.7),
            }}
          >
            {selectedUsers.map((userId) => {
              const user = users.find((u) => u._id === userId);
              const hasActiveSubscription = subscriptions.some(
                (sub) =>
                  sub.userId.toString() === userId && sub.status === 'active',
              );

              if (!user) return null;
              return (
                <Chip
                  key={user._id}
                  label={`${user.firstName} ${user.lastName}`}
                  onDelete={() => handleRemoveUser(userId)}
                  color={hasActiveSubscription ? 'success' : 'warning'}
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              );
            })}
          </Box>
        </Box>
      )}
      <Button
        variant="contained"
        color="primary"
        onClick={handleSave}
        sx={{ mt: 'auto', ml: 'auto' }} // This pushes the button to the bottom right
      >
        Speichern
      </Button>
    </Box>
  );
}
